import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, AlertCircle, Loader2, ExternalLink,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import {
  fetchTenantConnectStatus,
  startTenantConnectOnboarding,
  type TenantConnectStatus,
} from '@/lib/api';
import { PLATFORM_HELD_PAYOUTS_COPY, tenantPayoutsView } from '@/lib/tenantPayoutsView';
import styles from './PayoutsCompletePage.module.css';
import { CanadianPayoutNote } from '@/components/payouts/CanadianPayoutNote';

interface Props { onNavigate: (path: string) => void }

/**
 * Stripe-hosted onboarding redirects the operator here on
 * completion (or on AccountLink refresh). Reads the live tenant
 * Connect status and surfaces the appropriate next step:
 *
 *   - Ready: "Payouts active" + back-to-Settings CTA.
 *   - Pending: list requirements_currently_due (humanized) +
 *     "Continue setup" (mints a new AccountLink) + "Check status"
 *     (refetch).
 *
 * Operator-side mirror of the resident's ConnectCompletePage
 * (apps/web/src/pages/my-account/ConnectCompletePage.tsx). Same
 * state shape, same humanized requirements, same focus-refetch
 * pattern.
 *
 * The webhook flips Tenant.stripe_connect_charges_enabled when
 * Stripe confirms readiness. There can be a delivery lag between
 * the operator landing here and the webhook firing — the GET
 * /admin/tenants/:id/connect/status endpoint hits Stripe in
 * real-time and reconciles the cached flag, so this page reflects
 * truth even when the webhook is in transit.
 */
export function PayoutsCompletePage({ onNavigate }: Props) {
  const tenantId = useAuthStore((s) => s.user?.tenant_id);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tenant-connect-status', tenantId],
    queryFn: () => fetchTenantConnectStatus(tenantId!),
    enabled: !!tenantId,
    // Refetch on window focus — if the operator bounced to Stripe
    // and back via a different tab, the underlying state changes
    // and we want this page to reflect that without a manual reload.
    refetchOnWindowFocus: true,
  });
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState('');

  const handleResume = async (): Promise<void> => {
    if (!tenantId) return;
    setResuming(true);
    setResumeError('');
    try {
      const { url } = await startTenantConnectOnboarding(tenantId);
      window.location.href = url;
    } catch (err) {
      setResumeError(err instanceof Error ? err.message : 'Failed to start onboarding');
      setResuming(false);
    }
  };

  return (
    <div className={styles.page}>
      <CanadianPayoutNote />
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label="Back"
          onClick={() => onNavigate('/settings')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Payout setup</h1>
          <p className={styles.subtitle}>
            {isLoading ? 'Checking your status…' : data ? statusSubtitle(data) : ''}
          </p>
        </div>
      </header>

      {!tenantId && (
        <div className={styles.errorBox}>
          No tenant context. Sign in again to continue payout setup.
        </div>
      )}

      {tenantId && isLoading && <div className={styles.cardSkeleton} />}

      {tenantId && !isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : 'Failed to check status'}
        </div>
      )}

      {tenantId && !isLoading && !error && data && (
        <div className={styles.card}>
          {tenantPayoutsView(data) === 'platform_held' ? (
            // Ruling 2: the Api's platform_held flag alone. No Continue
            // button; the Api refuses /connect/start for this tenant.
            <p className={styles.cardBody}>{PLATFORM_HELD_PAYOUTS_COPY}</p>
          ) : tenantPayoutsView(data) === 'ready' ? (
            <ReadyState onContinue={() => onNavigate('/settings')} />
          ) : (
            <PendingState
              data={data}
              resuming={resuming}
              resumeError={resumeError}
              onResume={handleResume}
              onCheckAgain={() => refetch()}
            />
          )}
        </div>
      )}
    </div>
  );
}

function statusSubtitle(data: TenantConnectStatus): string {
  if (tenantPayoutsView(data) === 'platform_held') return 'Held by the EVI platform';
  if (!data.has_account) return 'Onboarding not started';
  if (data.charges_enabled && data.payouts_enabled) return 'Payout account active';
  return 'A few details still needed';
}

function ReadyState({ onContinue }: { onContinue: () => void }) {
  return (
    <>
      <div className={`${styles.iconWrap} ${styles.iconReady}`} aria-hidden>
        <CheckCircle2 size={28} />
      </div>
      <h2 className={styles.cardTitle}>You&rsquo;re ready to accept payouts</h2>
      <p className={styles.cardBody}>
        Your payout account is active. Session revenue (minus
        EVI&rsquo;s platform fee and any resident markup) will land
        in your Stripe account once Stripe schedules each payout.
        You can review payout detail any time from the Settings
        page&rsquo;s Payouts section.
      </p>
      <button type="button" className={styles.primaryBtn} onClick={onContinue}>
        Back to Settings
      </button>
    </>
  );
}

function PendingState({
  data, resuming, resumeError, onResume, onCheckAgain,
}: {
  data: TenantConnectStatus;
  resuming: boolean;
  resumeError: string;
  onResume: () => void;
  onCheckAgain: () => void;
}) {
  const dueCount = data.requirements_currently_due.length;
  return (
    <>
      <div className={`${styles.iconWrap} ${styles.iconPending}`} aria-hidden>
        <AlertCircle size={28} />
      </div>
      <h2 className={styles.cardTitle}>
        {!data.has_account ? 'Set up your payout account' : 'A few more details'}
      </h2>
      <p className={styles.cardBody}>
        {!data.has_account
          ? 'Connect a Stripe Express account to receive payouts from sessions on your chargers.'
          : dueCount > 0
            ? `Stripe still needs ${dueCount} item${dueCount === 1 ? '' : 's'} from you before payouts can start.`
            : 'Your account is being reviewed. This usually takes a minute or two.'}
      </p>

      {data.requirements_disabled_reason && (
        <p className={styles.disabledReason}>{data.requirements_disabled_reason}</p>
      )}

      {dueCount > 0 && (
        <ul className={styles.reqList}>
          {data.requirements_currently_due.slice(0, 6).map((req) => (
            <li key={req} className={styles.reqItem}>{humanizeReq(req)}</li>
          ))}
          {dueCount > 6 && (
            <li className={styles.reqMore}>+{dueCount - 6} more</li>
          )}
        </ul>
      )}

      {resumeError && (
        <p className={styles.errorInline}>{resumeError}</p>
      )}

      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={onResume}
          disabled={resuming}
        >
          {resuming
            ? <><Loader2 size={14} className={styles.spin} /> Opening Stripe…</>
            : <><ExternalLink size={14} /> Continue setup</>}
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onCheckAgain}
          disabled={resuming}
        >
          Check status
        </button>
      </div>
    </>
  );
}

/** Stripe requirement field names like `external_account` /
 *  `individual.dob.day` aren't friendly. Surface the cleaner
 *  section before the first dot, capitalized. Mirrors humanizeReq
 *  on the resident ConnectCompletePage. */
function humanizeReq(req: string): string {
  const head = req.split('.')[0] ?? req;
  const spaced = head.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
